import React, { useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, ArrowLeft } from "lucide-react";
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import AuthLeftPanel from "@/components/Auth/AuthLeftPanel";

const ForgotPassword = () => {
  const { toast } = useToast();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!email) {
      setErrorMsg("Por favor, digite seu email.");
      return;
    }

    setLoading(true);

    try {
      // Use edge function with Resend for reliable email delivery
      const { data, error } = await supabase.functions.invoke('send-password-reset', {
        body: { email }
      });

      if (error) {
        console.error('Error calling send-password-reset:', error);
        setErrorMsg("Não foi possível enviar o email de recuperação. Tente novamente.");
        toast({
          variant: "destructive",
          title: "Erro ao enviar email",
          description: "Ocorreu um erro inesperado. Tente novamente.",
        });
      } else if (data?.error) {
        setErrorMsg(data.error);
        toast({
          variant: "destructive",
          title: "Erro ao enviar email",
          description: data.error,
        });
      } else {
        setSuccess(true);
        toast({
          title: "Email enviado!",
          description: "Verifique sua caixa de entrada para redefinir sua senha.",
        });
      }
    } catch (err: any) {
      setErrorMsg(err.message || "Erro inesperado.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col md:flex-row">
      <AuthLeftPanel />
      <div className="flex-1 flex items-center justify-center bg-white min-h-screen md:min-h-0">
        <div className="w-full max-w-md p-8 md:py-16 md:px-8 shadow-none md:shadow-xl md:rounded-xl border-none bg-white animate-fade-in">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-1 text-zinc-900">Esqueci minha senha</h2>
            <p className="text-gray-600 text-sm">Digite seu email para receber instruções de recuperação.</p>
          </div>
          
          {success && (
            <Alert className="mb-4">
              <Mail className="h-4 w-4" />
              <AlertTitle>Email enviado!</AlertTitle>
              <AlertDescription>
                Verifique sua caixa de entrada e siga as instruções para redefinir sua senha.
              </AlertDescription>
            </Alert>
          )}
          
          {errorMsg && (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>Erro</AlertTitle>
              <AlertDescription>{errorMsg}</AlertDescription>
            </Alert>
          )}
          
          {!success ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    name="email"
                    id="email"
                    type="email"
                    placeholder="Digite seu email"
                    className="pl-10"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrorMsg(null);
                    }}
                    required
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-2 rounded-md transition disabled:opacity-60" 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  "Enviar email de recuperação"
                )}
              </Button>
            </form>
          ) : null}
          
          <div className="mt-6 text-center">
            <Link 
              to="/auth" 
              className="inline-flex items-center text-sm text-primary hover:text-primary/80 font-medium"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Voltar para o login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;